// FigmaCard.tsx
// This component displays a card, showing a fund name, type, and a bookmark icon. It is responsive, accessible, and customizable via props.
import React from 'react'
import { motion } from 'framer-motion'
import BookmarkIcon from '../../icons/BookmarkIcon'
import { getMutualFundDetailCTA } from '../../utils/mutualFundDetail'
import { postCTA, trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'

interface FigmaCardProps {
  /** Main title, e.g., fund name */
  title?: string
  /** Subtitle, e.g., fund type */
  subtitle?: string
  /** Whether the card is selected (shows bookmark, highlighted style) */
  selected?: boolean
  /** Optional className for custom styling */
  className?: string
  isUsersFund?: boolean
  /** Callback function when bookmark icon is clicked */
  onBookmarkClick?: () => void
  /** ISIN of the fund for navigation */
  isin?: string
  throughFamilyAccess?: boolean
}

const MFNameCard: React.FC<FigmaCardProps> = ({
  title = 'Nippon India',
  selected = false,
  className = '',
  isUsersFund = false,
  onBookmarkClick,
  isin,
  throughFamilyAccess
}) => {
  const handleCardClick = () => {
    if (isin) {
      // Track fund card click
      trackEvent({
        eventName: EventNames.UserClicked,
        payload: {
          ScreenName: 'PeerAnalyser',
          fundName: title,
          eventId: isin
        }
      })

      const cta = getMutualFundDetailCTA({ isin, navigationStyle: 'PUSH' })
      // You can handle the CTA here or pass it to a parent component
      console.log('CTA for navigation:', cta)
      // For now, we'll just log it. You might want to dispatch this to your navigation system
      return postCTA(JSON.stringify(cta))
    }
  }

  const handlePinClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()

    // Track bookmark action
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'PeerAnalyser',
        fundName: title,
        eventId: isin || '',
        buttonLabel: selected ? 'Unpin' : 'Pin'
      }
    })

    onBookmarkClick?.()
  }

  const fundStatusLabel = isUsersFund ? (throughFamilyAccess ? 'Active fund' : 'Your fund') : undefined
  const openLabelBase = fundStatusLabel ? `${fundStatusLabel}, ${title}` : title
  // Power select_B10 (iOS): include interaction hint in accessible name for VoiceOver
  const openLabel = `${openLabelBase}, double tap to view fund details`
  // Power Select_B09–B10: state-aware pin name + button role (native <button>)
  const pinLabel = selected ? `Unpin fund, ${title}` : `Pin fund to top, ${title}`

  return (
    <div
      className={`z-1 ${
        selected && 'bg-[#E6F2FF99]'
      } relative w-full max-w-[150px] transition-colors duration-200 ${className}`}
    >
      <div className='relative flex flex-col justify-center h-16'>
        <div className='flex items-center w-full justify-center h-full'>
          <div className='relative h-full flex-shrink-0 w-[34px]'>
            <motion.button
              type='button'
              aria-label={pinLabel}
              aria-pressed={selected}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              onClick={handlePinClick}
              className='cursor-pointer h-full w-full flex justify-center items-center pl-2 border-none bg-transparent p-0'
            >
              <BookmarkIcon selected={selected} className='w-4 h-4' />
            </motion.button>
          </div>
          <button
            type='button'
            aria-label={openLabel}
            onClick={handleCardClick}
            className='flex flex-col pr-3 ml-[6px] border-none bg-transparent p-0 text-left cursor-pointer'
          >
            {isUsersFund && (
              <span aria-hidden='true' className='text-[11px] text-blue30 font-medium leading-1'>
                {throughFamilyAccess ? 'Active fund' : 'Your fund'}
              </span>
            )}
            <span
              aria-hidden='true'
              className='max-w-[80px] text-[#232323] text-xs line-clamp-2 overflow-hidden text-ellipsis'
              title={title}
            >
              {title}
            </span>
          </button>
        </div>
      </div>
      {/* Decorative gradient bar on the right (only in selected state) */}
      {selected && (
        <div
          className='absolute top-[-2px] bottom-[-1px] right-[-1px] w-8 bg-gradient-to-r from-transparent to-[#eff7ff] pointer-events-none'
          aria-hidden='true'
        />
      )}
    </div>
  )
}

export default MFNameCard
